import {
  arrivalClass,
  BarChart,
  buttonClass,
  Card,
  cx,
  EmptyState,
  IconTile,
  PageHeader,
  RollingNumber,
  Skeleton,
  Stat,
  StateSwap,
  TD,
  TH,
  TR,
  Table,
  useArrivals,
} from '@ims/ui'
import {
  AlertTriangle,
  Bike,
  CheckCircle2,
  ChevronRight,
  CookingPot,
  FileText,
  HandCoins,
  Package,
  ShoppingBag,
  ShoppingCart,
  TrendingUp,
  UtensilsCrossed,
  X,
} from '@ims/ui/icons'
import { orderTotals } from '@ims/core'
import type { Area } from '@ims/ui'
import { useState, type ComponentType, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useBusinessMode, useBusinessName, useLive, useSettings } from '../hooks.ts'
import { dateLabel, money, todayIso } from '../lib/format.ts'
import { firstTimeToday, moveClick, useOpen } from '../lib/motion.ts'
import { registrationOf, taxNumberLabel } from '../lib/registration.ts'
import {
  addDays,
  bucketSales,
  calendarOf,
  daysBetween,
  monthStartOf,
  productLines,
  salesByHour,
  salesByService,
  salesSummary,
  type ServiceKey,
  type SalesSummary,
} from '../lib/reports.ts'
import { useCan, useWorkspace } from '../workspace.tsx'
import { CANCELLED_ROW, StatusBadge, balanceOf, useOpenBalances } from './invoices.tsx'
import { useAlerts } from '../layout/alerts.tsx'
import { StorageTipCard } from '../layout/storage.tsx'
import { orderItemRows, tablesPath } from '../lib/orders.ts'
import { ridersCashTotal, useActiveDeliveries } from './deliveries.tsx'
import { useExpiringBatches } from './stock.tsx'

/** How each way of serving shows in "Sales by service". */
const SERVICE: Record<
  ServiceKey,
  { label: string; area: Area; icon: ComponentType<{ className?: string }> }
> = {
  dine_in: { label: 'Dine in', area: 'kitchen', icon: UtensilsCrossed },
  takeaway: { label: 'Takeaway', area: 'take', icon: ShoppingBag },
  delivery: { label: 'Delivery', area: 'insight', icon: Bike },
  counter: { label: 'Counter', area: 'sell', icon: ShoppingCart },
}

/** A stat card that opens its detail, for those who may see it (`to` null: just the card). */
function StatLink({ to, children }: { to: string | null; children: ReactNode }) {
  if (!to) return children
  return (
    <Link
      to={to}
      className="block rounded-card transition-shadow hover:shadow-card-hover [&>*]:h-full"
    >
      {children}
    </Link>
  )
}

export function DashboardScreen() {
  const settings = useSettings()
  const openItem = useOpen()
  // The month's chart rises once a day on this device; after that it only eases to new values.
  const [growChart] = useState(() => firstTimeToday('dashboard-chart'))
  const { current } = useWorkspace()
  const canReports = useCan('manager')
  // What is owed to suppliers: one statement, managers only.
  const payables = useLive(
    (repo) =>
      canReports && repo.payables ? repo.payables.totals(todayIso()) : Promise.resolve(null),
    [canReports],
    ['supplier_bills', 'supplier_payments', 'supplier_credits', 'supplier_allocations'],
  ).data
  const businessName = useBusinessName(current?.tenant.name)
  const today = todayIso()
  // Nepali or English month, as in Reports (Settings → Show dates in).
  const monthStart = monthStartOf(today, settings)
  // The same number of days at the start of last month, to compare like with like.
  const lastMonthEnd = addDays(monthStart, -1)
  const prevStart = monthStartOf(lastMonthEnd, settings)
  const prevTo = addDays(prevStart, daysBetween(monthStart, today))
  const prevEnd = prevTo > lastMonthEnd ? lastMonthEnd : prevTo
  const { restaurant } = useBusinessMode()
  const alerts = useAlerts()
  // Restaurant mode: the open orders, and today's bills by service and by hour.
  const service = useLive(
    async (repo) => {
      if (!restaurant) return null
      const [orders, sales] = await Promise.all([
        repo.orders.open(),
        canReports ? repo.reports.daySales(today) : [],
      ])
      const tickets = orders.flatMap((o) => o.tickets.filter((t) => !t.deleted_at))
      return {
        open: orders.length,
        unbilled: orders.reduce(
          (sum, o) => sum + orderTotals(orderItemRows(o.items)).totalPaisa,
          0,
        ),
        cooking: tickets.filter((t) => t.status !== 'ready').length,
        byService: salesByService(sales),
        byHour: salesByHour(sales, new Date().getHours()),
      }
    },
    [restaurant, canReports, today],
    ['orders', 'order_items', 'kot_tickets', 'sales_invoices'],
  ).data
  const deliveryOn = restaurant && !!settings?.delivery_product_id
  const active = useActiveDeliveries()
  const withRiders = deliveryOn && active ? ridersCashTotal(active) : null
  const open = useOpenBalances()
  // Paperwork that is waiting on someone: quotations out, orders to bill, deliveries to come.
  const waiting = useLive(
    async (repo) => {
      if (!repo.reports) return null
      const [quotes, orders, purchases] = await Promise.all([
        restaurant ? null : repo.salesOrders.list({ kind: 'quotation', status: 'sent', limit: 1 }),
        restaurant ? [] : repo.reports.openSalesOrders(),
        repo.reports.openPurchaseOrders(),
      ])
      return { quotes: quotes?.total ?? 0, orders: orders.length, purchases: purchases.length }
    },
    [restaurant],
    [
      'sales_orders',
      'sales_order_lines',
      'sales_invoices',
      'sales_invoice_lines',
      'purchase_orders',
      'purchase_order_lines',
      'goods_receipt_lines',
    ],
  ).data
  const expiring = useExpiringBatches()
  const expired = (expiring ?? []).filter((r) => r.status === 'expired')
  const soon = (expiring ?? []).filter((r) => r.status === 'soon')

  const stats = useLive(
    async (repo) => {
      const [days, recent, products, levels, anyProduct, prevDays, sold] = await Promise.all([
        repo.reports.salesByDay(monthStart, today),
        repo.invoices.list({ limit: 8 }),
        repo.list('products', { where: { track_stock: true, is_active: true }, limit: 10000 }),
        repo.stock.levels(),
        repo.list('products', { limit: 1 }),
        // The chart and the top sellers are for people who can open Reports.
        canReports ? repo.reports.salesByDay(prevStart, prevEnd) : [],
        canReports ? repo.reports.productSales(monthStart, today) : [],
      ])
      const totals = new Map<string, number>()
      for (const l of levels)
        totals.set(l.product_id, (totals.get(l.product_id) ?? 0) + l.qty_milli)
      const low = products.rows.filter(
        (p) =>
          (totals.get(p.id) ?? 0) <= p.min_stock_milli &&
          (p.min_stock_milli > 0 || (totals.get(p.id) ?? 0) <= 0),
      )
      const negative = products.rows.filter((p) => (totals.get(p.id) ?? 0) < 0).length
      return {
        negative,
        // Net of returns, like Reports.
        today: salesSummary(days.filter((d) => d.date === today)),
        month: salesSummary(days),
        days,
        prevNet: salesSummary(prevDays).net,
        top: productLines(sold, new Map(products.rows.map((p) => [p.id, p.name])), 'amount')
          .lines.filter((l) => l.net > 0)
          .slice(0, 5),
        low,
        productCount: products.total,
        hasProducts: anyProduct.total > 0,
        recent: recent.rows,
      }
    },
    [today, monthStart, prevStart, prevEnd, canReports],
    ['sales_invoices', 'credit_notes', 'products', 'stock_movements'],
  ).data
  // A bill that reaches the list after it was shown (made on another device) slides in.
  const arrived = useArrivals(stats?.recent.map((i) => i.id) ?? [], stats !== undefined)
  const m = (paisa: number) => money(paisa, settings)
  /** "3 invoices", or "Rs. 452.00 billed, Rs. 113.00 returned" when something came back. */
  const salesHint = (s: SalesSummary) =>
    s.returns > 0
      ? `${m(s.sales)} billed, ${m(s.returns)} returned`
      : `${s.invoices} invoice${s.invoices === 1 ? '' : 's'}`
  // Balances need each invoice's payments: slower, so the other cards don't wait for it.
  const owed = useLive(
    async (repo) => {
      const month = await repo.invoices.list({
        status: 'finalized',
        from: monthStart,
        to: today,
        limit: 500,
      })
      const details = await Promise.all(month.rows.map((i) => repo.invoices.detail(i.id)))
      return details.reduce((a, d) => a + Math.max(0, d?.balancePaisa ?? 0), 0)
    },
    [today, monthStart],
    ['sales_invoices', 'payments', 'payment_allocations', 'credit_notes'],
  ).data

  const buckets = stats
    ? bucketSales(stats.days, { from: monthStart, to: today }, 'day', calendarOf(settings))
    : []
  /** "12% more than the same days last month", or nothing when last month had no sales. */
  const versusLastMonth = (() => {
    if (!stats || stats.prevNet <= 0) return null
    const change = Math.round(((stats.month.net - stats.prevNet) / stats.prevNet) * 100)
    if (change === 0) return 'The same as the same days last month.'
    return `${Math.abs(change)}% ${change > 0 ? 'more' : 'less'} than the same days last month (${m(stats.prevNet)}).`
  })()

  return (
    <div>
      <PageHeader
        title={`Namaste, ${businessName}`}
        subtitle={dateLabel(today, settings)}
        actions={
          // Phones have Bill in the bottom bar.
          <Link to="/bill" className={buttonClass({ className: 'max-sm:hidden' })}>
            <ShoppingCart className="size-4" aria-hidden />
            New bill
          </Link>
        }
      />
      {stats && stats.negative > 0 && (
        <Card className="mb-4 flex flex-wrap items-center gap-3 border-danger/30">
          <AlertTriangle className="size-5 shrink-0 text-danger" />
          <p className="flex-1 text-sm">
            <span className="font-semibold">
              {stats.negative} product{stats.negative === 1 ? ' is' : 's are'} below zero in stock.
            </span>{' '}
            <span className="text-muted">
              Usually a delivery that wasn’t entered, or a count that needs fixing.
            </span>
          </p>
          <Link
            to="/stock?show=negative"
            className="text-sm font-medium text-primary hover:underline"
          >
            Review
          </Link>
        </Card>
      )}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatLink to={canReports ? '/reports?period=today' : null}>
          <Stat
            label="Sales today"
            area="sell"
            value={stats && <RollingNumber value={stats.today.net} format={m} />}
            hint={stats && salesHint(stats.today)}
            icon={<TrendingUp className="size-5" />}
          />
        </StatLink>
        <StatLink to={canReports ? '/reports' : null}>
          <Stat
            label="This month"
            area="sell"
            value={stats && <RollingNumber value={stats.month.net} format={m} />}
            hint={stats && salesHint(stats.month)}
            icon={<FileText className="size-5" />}
          />
        </StatLink>
        {restaurant && (
          <StatLink to="/orders">
            <Stat
              label="Open orders"
              area="kitchen"
              value={service && <RollingNumber value={service.open} format={String} />}
              hint={
                alerts.count > 0
                  ? `${alerts.count} waiting: guests’ requests and ready tickets`
                  : service && service.open > 0
                    ? `${m(service.unbilled)} to bill${service.cooking > 0 ? `, ${service.cooking} in the kitchen` : ''}`
                    : 'Nothing waiting'
              }
              icon={<UtensilsCrossed className="size-5" />}
            />
          </StatLink>
        )}
        {deliveryOn ? (
          <StatLink to={tablesPath({ service_type: 'delivery' })}>
            <Stat
              label="Cash with riders"
              area="take"
              value={
                withRiders ? <RollingNumber value={withRiders.duePaisa} format={m} /> : undefined
              }
              hint={
                withRiders &&
                (withRiders.deliveries > 0
                  ? `From ${withRiders.deliveries} deliver${withRiders.deliveries === 1 ? 'y' : 'ies'}`
                  : 'Nothing to collect')
              }
              icon={<Bike className="size-5" />}
            />
          </StatLink>
        ) : (
          <StatLink to={canReports ? '/reports?tab=dues' : null}>
            <Stat
              label="Owed this month"
              area="people"
              value={owed !== undefined ? <RollingNumber value={owed} format={m} /> : undefined}
              hint="Unpaid balances on this month’s invoices"
              icon={<HandCoins className="size-5" />}
            />
          </StatLink>
        )}
        {!restaurant && (
          <StatLink to="/stock">
            <Stat
              label="Low or out of stock"
              area="stock"
              value={stats && <RollingNumber value={stats.low.length} format={String} />}
              hint={stats && `of ${stats.productCount} tracked products`}
              icon={<AlertTriangle className="size-5" />}
            />
          </StatLink>
        )}
      </div>
      {payables && payables.owedPaisa > 0 && (
        <Link
          to="/reports?tab=payables"
          className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card bg-surface px-4 py-3 shadow-card transition-shadow hover:shadow-card-hover"
        >
          <IconTile area="buy">
            <HandCoins className="size-5" />
          </IconTile>
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-muted">To pay suppliers</span>
            <span className="tabular block text-lg leading-tight font-bold text-ink">
              {money(payables.owedPaisa, settings)}
            </span>
          </span>
          {payables.overduePaisa > 0 && (
            <span className="text-sm font-semibold text-danger">
              {money(payables.overduePaisa, settings)} is late
            </span>
          )}
        </Link>
      )}
      {waiting && waiting.quotes + waiting.orders + waiting.purchases > 0 && (
        <Card className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-3 text-sm">
          <span className="font-medium">Waiting</span>
          {waiting.quotes > 0 && (
            <Link to="/quotations" className="text-primary hover:underline">
              {waiting.quotes} quotation{waiting.quotes === 1 ? '' : 's'} with customers
            </Link>
          )}
          {waiting.orders > 0 && (
            <Link to="/orders" className="text-primary hover:underline">
              {waiting.orders} order{waiting.orders === 1 ? '' : 's'} to bill
            </Link>
          )}
          {waiting.purchases > 0 && (
            <Link to="/purchase-orders" className="text-primary hover:underline">
              {waiting.purchases} purchase order{waiting.purchases === 1 ? '' : 's'} to arrive
            </Link>
          )}
        </Card>
      )}
      <div className="mt-4 empty:hidden [&>*:last-child]:mb-0">
        {stats && settings && (
          <SetupChecklist
            tenantId={current?.tenant_id ?? ''}
            steps={[
              {
                label: 'Add your business details',
                detail: `${taxNumberLabel(registrationOf(settings))} and address, printed on every bill`,
                done: !!settings.pan_vat_number?.trim(),
                to: '/settings?tab=business',
              },
              {
                label: 'Add or import your products',
                detail: 'Type them in, scan barcodes, or paste from Excel',
                done: stats.hasProducts,
                to: '/products',
              },
              {
                label: 'Make your first bill',
                detail: 'Scan or search, then press F9',
                done: stats.recent.length > 0,
                to: '/bill',
              },
            ]}
          />
        )}
        <StorageTipCard />
      </div>
      {!stats ? (
        <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_340px]">
          <SkeletonCard rows={5} />
          <SkeletonCard rows={4} />
        </div>
      ) : (
        <>
          {canReports && service && service.byService.length > 0 && (
            <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
              <Card className="p-0">
                <div className="border-b border-border px-5 py-3">
                  <h2 className="font-semibold">Sales by service today</h2>
                </div>
                <ul className="divide-y divide-border">
                  {service.byService.map((row) => {
                    const { label, area, icon: Icon } = SERVICE[row.key]
                    return (
                      <li key={row.key} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                        <IconTile area={area}>
                          <Icon className="size-5" />
                        </IconTile>
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium">{label}</span>
                          <span className="block text-xs text-subtle">
                            {row.bills} bill{row.bills === 1 ? '' : 's'}
                          </span>
                        </span>
                        <span className="tabular shrink-0 font-semibold">{m(row.total)}</span>
                      </li>
                    )
                  })}
                </ul>
              </Card>
              <Card className="p-0">
                <div className="border-b border-border px-5 py-3">
                  <h2 className="font-semibold">Sales by hour today</h2>
                </div>
                <div className="p-4">
                  <BarChart
                    data={service.byHour.map((h) => ({ label: h.label, value: h.total }))}
                    format={m}
                    highlight={service.byHour.length - 1}
                    summary={`Bills by the hour today, ${service.byHour[0]?.label} to ${service.byHour.at(-1)?.label}.`}
                    height={140}
                  />
                </div>
              </Card>
            </div>
          )}
          {canReports && (stats.month.invoices > 0 || stats.month.returns > 0) && (
            <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              <Card className="p-0">
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                  <h2 className="font-semibold">Sales this month</h2>
                  <Link to="/reports" className="text-sm text-primary hover:underline">
                    Reports
                  </Link>
                </div>
                <div className="p-4">
                  <BarChart
                    data={buckets.map((b) => ({ label: b.label, value: b.net }))}
                    format={m}
                    highlight={buckets.length - 1}
                    summary={`Net sales by day this month: ${m(stats.month.net)} in total.`}
                    height={140}
                    grow={growChart}
                  />
                  {versusLastMonth && <p className="mt-3 text-sm text-muted">{versusLastMonth}</p>}
                </div>
              </Card>
              <Card className="p-0">
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                  <h2 className="font-semibold">Top sellers this month</h2>
                  <Link to="/reports?tab=products" className="text-sm text-primary hover:underline">
                    All
                  </Link>
                </div>
                <ol className="divide-y divide-border">
                  {stats.top.map((l) => (
                    <li key={l.key} className="px-5 py-2.5 text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="min-w-0 truncate font-medium">{l.name}</span>
                        <span className="tabular shrink-0">{m(l.net)}</span>
                      </div>
                      <div
                        aria-hidden
                        className="mt-1.5 h-1 rounded-full bg-primary/70"
                        style={{ width: `${Math.max(4, (l.net / stats.top[0]!.net) * 100)}%` }}
                      />
                    </li>
                  ))}
                </ol>
              </Card>
            </div>
          )}
          {(expired.length > 0 || soon.length > 0) && (
            <Card className="mt-4 p-0">
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <h2 className="font-semibold">
                  Expiry{' '}
                  <span className="text-sm font-normal text-muted">
                    {expired.length > 0 && `${expired.length} expired`}
                    {expired.length > 0 && soon.length > 0 && ' · '}
                    {soon.length > 0 &&
                      `${soon.length} expiring within ${settings?.expiry_alert_days ?? 30} days`}
                  </span>
                </h2>
                <Link to="/stock" className="text-sm text-primary hover:underline">
                  Stock
                </Link>
              </div>
              <ul className="divide-y divide-border">
                {[...expired, ...soon].slice(0, 6).map((r) => (
                  <li key={r.batch.id}>
                    <Link
                      to={`/products/${r.product.id}`}
                      className="flex w-full flex-wrap justify-between gap-2 px-5 py-2.5 text-left text-sm hover:bg-soft"
                    >
                      <span>
                        <span className="font-medium">{r.product.name}</span>
                        <span className="ml-2 text-muted">batch {r.batch.batch_no}</span>
                      </span>
                      <span className={r.status === 'expired' ? 'text-danger' : 'text-warning'}>
                        {r.status === 'expired'
                          ? `expired ${r.batch.expiry_date}`
                          : `expires ${r.batch.expiry_date}`}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_340px]">
            <Card className="p-0">
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <h2 className="font-semibold">Recent invoices</h2>
                <Link to="/invoices" className="text-sm text-primary hover:underline">
                  View all
                </Link>
              </div>
              {stats.recent.length === 0 ? (
                <EmptyState
                  icon={<FileText className="size-6" />}
                  title="No invoices yet"
                  description="Make your first sale from Quick bill."
                />
              ) : (
                <Table stack>
                  <thead>
                    <tr>
                      <TH>Number</TH>
                      <TH>Customer</TH>
                      <TH>Status</TH>
                      <TH align="right">Total</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent.map((inv) => (
                      <TR
                        key={inv.id}
                        onClick={() => openItem(`/invoices/${inv.id}`)}
                        className={cx(
                          inv.status === 'cancelled' && CANCELLED_ROW,
                          arrived.has(inv.id) && arrivalClass,
                        )}
                      >
                        <TD className="stack-main stack-r1 font-medium">
                          <Link
                            to={`/invoices/${inv.id}`}
                            className="hover:underline focus-visible:underline"
                            onClick={(e) => {
                              e.stopPropagation()
                              moveClick(`/invoices/${inv.id}`, 'open')(e)
                            }}
                          >
                            {inv.number ?? 'Not numbered yet'}
                          </Link>
                        </TD>
                        <TD className="stack-main stack-r2 max-sm:text-muted">
                          {inv.customer_name ?? <span className="text-muted">Walk-in</span>}
                        </TD>
                        <TD className="stack-end stack-r2">
                          <StateSwap state={`${inv.status}:${balanceOf(open, inv.id)}`}>
                            <StatusBadge invoice={inv} balancePaisa={balanceOf(open, inv.id)} />
                          </StateSwap>
                        </TD>
                        <TD align="right" className="stack-end stack-r1 max-sm:font-medium">
                          {money(inv.total_paisa, settings)}
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
            <Card className="p-0">
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <h2 className="font-semibold">Needs restocking</h2>
                <Link to="/stock" className="text-sm text-primary hover:underline">
                  Stock
                </Link>
              </div>
              {stats.low.length === 0 ? (
                <EmptyState
                  icon={
                    restaurant ? <CookingPot className="size-6" /> : <Package className="size-6" />
                  }
                  title="All stocked up"
                />
              ) : (
                <ul className="divide-y divide-border">
                  {stats.low.slice(0, 8).map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/products/${p.id}`}
                        className="flex w-full justify-between px-5 py-2.5 text-left text-sm hover:bg-soft"
                      >
                        <span className="font-medium">{p.name}</span>
                        <span className="text-muted">{p.sku}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}

function SkeletonCard({ rows }: { rows: number }) {
  return (
    <Card className="space-y-4" aria-busy="true">
      <Skeleton className="h-4 w-36" />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-9 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-3/5" />
            <Skeleton className="h-2.5 w-2/5" />
          </div>
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </Card>
  )
}

interface SetupStep {
  label: string
  detail: string
  done: boolean
  to: string
}

/** First-run steps, worked out from the data. Hidden once done or dismissed on this device. */
function SetupChecklist({ tenantId, steps }: { tenantId: string; steps: SetupStep[] }) {
  const key = `ims.setup-dismissed.${tenantId}`
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(key) === '1'
    } catch {
      return false
    }
  })
  const done = steps.filter((s) => s.done).length
  if (dismissed || done === steps.length) return null
  const dismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(key, '1')
    } catch {
      // Storage blocked: hidden until the next visit.
    }
  }
  return (
    <Card className="mb-4 animate-fade-in">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Get your shop ready</h2>
          <p className="text-sm text-muted">
            {done} of {steps.length} done
          </p>
        </div>
        <button
          type="button"
          aria-label="Hide these steps"
          onClick={dismiss}
          className="rounded-lg p-1 text-muted hover:bg-soft hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <div
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-primary-light"
        role="progressbar"
        aria-label="Setup progress"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={done}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${(done / steps.length) * 100}%` }}
        />
      </div>
      <ul className="mt-2 divide-y divide-border">
        {steps.map((s) => (
          <li key={s.label}>
            {s.done ? (
              <div className="flex items-center gap-3 py-2.5 text-sm text-muted">
                <CheckCircle2 className="size-5 shrink-0 text-success" />
                <span className="line-through">{s.label}</span>
              </div>
            ) : (
              <Link
                to={s.to}
                className="group flex items-center gap-3 py-2.5 text-sm hover:text-primary"
              >
                <span className="size-5 shrink-0 rounded-full border-2 border-primary/40" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{s.label}</span>
                  <span className="block text-xs text-muted">{s.detail}</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted transition-transform group-hover:translate-x-0.5" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Card>
  )
}
